import React, { useState, useEffect } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import Container from 'react-bootstrap/Container';
import { useParams } from 'react-router-dom';
import archPicture from '../../../assets/images/arch.jpg';
import vaadPicture from '../../../assets/images/vaad.jpg';
import Image from 'react-bootstrap/Image';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';

const kosherRestaurants = [
  {
    name: 'Gokul Indian Restaurant',
    url: 'http://www.gokulrestaurant.com/',
    description: 'Vegetarian Indian cuisine with Vaad supervision.'
  },
  {
    name: 'Elefant Pizza',
    description: 'Local pizza; call (314) 513-8149 to order.'
  },
  {
    name: 'Circle @ Crown Café',
    url: 'https://crowncenterstl.org/cafe/',
    description: 'Dairy café located in the Crown Center for Senior Living.'
  },
  {
    name: 'Corner Cafe',
    url: 'https://www.greentrailscornercafe.com/',
    description: 'Pizza, sushi, ice cream, and coffee in Chesterfield.'
  },
  {
    name: 'Bagels and Bliss',
    url: 'https://bagels-and-bliss.com/',
    description: 'NY-style bagels, sandwiches, and baked goods in University City.'
  },
  {
    name: "Nate's",
    url: 'https://www.facebook.com/eatnates/',
    description: 'Take-out deli.'
  },
  {
    name: "Ratner's",
    description: 'Meat, delivery, and catering. Call (314) 707-3646 to order.'
  }
];

const kosherMarkets = [
  {
    name: 'Schnucks Ladue and Dierbergs (University City)',
    description: 'Full-service supermarkets with expansive kosher aisles.'
  },
  {
    name: "Clementine's",
    url: 'https://www.clementinescreamery.com/pages/clayton',
    description: 'Ice cream parlor in Clayton.'
  },
  {
    name: 'Ted Drewes Frozen Custard',
    url: 'https://teddrewes.com/',
    description: 'A St. Louis staple offering Vaad-certified frozen treats.'
  },
  {
    name: 'Mound City Shelled Nut Company',
    url: 'https://www.moundcity.com/',
    description: 'Locally roasted nuts and snacks under Vaad supervision.'
  }
];

const communityHighlights = [
  'A welcoming Jewish population of roughly 60,000 spread across several nearby neighborhoods.',
  'Robust career opportunities in health care, finance, geospatial technology, and growing start-up sectors.',
  'Affordable housing, short commutes, and easy access to the airport—perfect for balancing family and work life.',
  'Seven Orthodox shuls, an eruv, mikvaot, day schools, and a variety of youth movements serving every age.'
];

const resourceGroups = [
  {
    title: 'Day Schools & Learning',
    items: [
      {
        name: 'Epstein Hebrew Academy',
        url: 'https://www.epsteinhebrewacademy.org/'
      },
      {
        name: 'Yeshivat Kadimah High School',
        url: 'https://www.yeshivatkadimah.org/'
      }
    ]
  },
  {
    title: 'Youth & Family',
    items: [
      { name: 'JSU', url: 'https://stl.jsu.org/' },
      { name: 'St. Louis NCSY', url: 'https://southwest.ncsy.org/stlouis/' },
      { name: 'Bnei Akiva of St. Louis', url: 'https://www.bneiakiva.org/' },
      { name: 'Camp Nageela Midwest', url: 'https://www.nageelamidwest.org/' }
    ]
  },
  {
    title: 'Community Services',
    items: [
      { name: 'Vaad Hoeir of St. Louis', url: 'https://ovkosher.org/' },
      { name: 'St. Louis Eruv', url: 'https://stleruv.org/' },
      { name: 'Tomchei Shabbos of St. Louis', url: 'https://www.tomcheistl.org/' }
    ]
  }
];

const visitTips = [
  {
    title: 'Hospitality',
    description:
      'Contact the shul office for Shabbat meals, home hospitality, and guidance on accommodations within the eruv.'
  },
  {
    title: 'Transportation',
    description:
      'We are 10 minutes from St. Louis Lambert International Airport and close to Metrolink transit and major highways.'
  },
  {
    title: 'Neighborhood Essentials',
    description:
      'Mikveh, eruv, and kosher shopping are all within a short walk or drive of the shul. Reach out for up-to-date hours and access information.'
  }
];

const Community = () => {
  const { currentTab } = useParams();
  const [activeTabIndex, setActiveTabIndex] = useState();

  const tabs = ['', 'kosher', 'resources', 'visiting'];

  useEffect(() => {
    setActiveTabIndex(!currentTab ? 0 : tabs.findIndex((el) => el === currentTab));
  }, []);

  const handleTabChange = (newTabIndex) => {
    setActiveTabIndex(newTabIndex);
    if (newTabIndex === 0) {
      window.history.replaceState(null, null, '/community');
    } else {
      window.history.replaceState(null, null, `/community/${tabs[newTabIndex]}`);
    }
  };

  return (
    <>
      <div className='page-header text-center d-flex container-fluid justify-content-center align-items-center'>
        <h1>Community</h1>
      </div>
      <TabView
        className='mt-1 nav-ribbon'
        activeIndex={activeTabIndex}
        onTabChange={(e) => handleTabChange(e.index)}
        scrollable
      >
        <TabPanel header='About The Community'>
          <Container className='py-4'>
            <Row className='align-items-center gy-4'>
              <Col lg={5}>
                <Image
                  src={archPicture}
                  fluid
                  rounded
                  alt='Gateway Arch overlooking downtown St. Louis'
                  className='shadow-sm'
                />
              </Col>
              <Col lg={7}>
                <p>
                  St. Louis offers a thriving Jewish community in a family-friendly, affordable city. Neighborhoods near the shul
                  brim with parks, playgrounds, and strong civic amenities, making it easy to balance career, learning, and family life.
                </p>
                <ul className='ps-3'>
                  {communityHighlights.map((highlight, index) => (
                    <li key={index}>{highlight}</li>
                  ))}
                </ul>
              </Col>
            </Row>
          </Container>
        </TabPanel>

        <TabPanel header='Kosher'>
          <Container className='py-4'>
            <Row className='gy-4 align-items-start'>
              <Col lg={4}>
                <Image
                  src={vaadPicture}
                  fluid
                  rounded
                  alt='Vaad Hoeir of St. Louis logo'
                  className='shadow-sm mb-3 mb-lg-0'
                  style={{ maxWidth: '160px' }}
                />
              </Col>
              <Col lg={8}>
                <h2 className='fw-bold text-center text-lg-start'>Vaad Hoeir of St. Louis</h2>
                <p>
                  <a href='https://ovkosher.org/' target='_blank' rel='noreferrer'>The Vaad Hoeir</a> oversees kashrut supervision for
                  restaurants, caterers, and manufacturers across the region. Look for the &quot;OV&quot; symbol on local products, and visit the
                  Vaad website for current alerts and guides.
                </p>
              </Col>
            </Row>
            <Row className='mt-4 g-4'>
              <Col md={6}>
                <h3>Restaurants &amp; Cafés</h3>
                <ul className='ps-3'>
                  {kosherRestaurants.map((restaurant) => (
                    <li key={restaurant.name} className='mb-2'>
                      {restaurant.url ? (
                        <a href={restaurant.url} target='_blank' rel='noreferrer'>
                          {restaurant.name}
                        </a>
                      ) : (
                        <span>{restaurant.name}</span>
                      )}
                      {restaurant.description && <span>: {restaurant.description}</span>}
                    </li>
                  ))}
                </ul>
              </Col>
              <Col md={6}>
                <h3>Markets &amp; Specialty Shops</h3>
                <ul className='ps-3'>
                  {kosherMarkets.map((market) => (
                    <li key={market.name} className='mb-2'>
                      {market.url ? (
                        <a href={market.url} target='_blank' rel='noreferrer'>
                          {market.name}
                        </a>
                      ) : (
                        <span>{market.name}</span>
                      )}
                      {market.description && <span>: {market.description}</span>}
                    </li>
                  ))}
                </ul>
              </Col>
            </Row>
          </Container>
        </TabPanel>

        <TabPanel header='Jewish Resources'>
          <Container className='py-4'>
            <Row className='g-4'>
              {resourceGroups.map((group) => (
                <Col md={4} key={group.title}>
                  <Card className='h-100 shadow-sm'>
                    <Card.Body>
                      <Card.Title>{group.title}</Card.Title>
                      <ul className='ps-3 mb-0'>
                        {group.items.map((item) => (
                          <li key={item.name} className='mb-2'>
                            <a href={item.url} target='_blank' rel='noreferrer'>
                              {item.name}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Container>
        </TabPanel>

        <TabPanel header='Visiting St. Louis'>
          <Container className='py-4'>
            <Row className='g-4'>
              {visitTips.map((tip) => (
                <Col md={4} key={tip.title}>
                  <Card className='h-100 shadow-sm'>
                    <Card.Body>
                      <Card.Title>{tip.title}</Card.Title>
                      <Card.Text>{tip.description}</Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
            <Row className='mt-4'>
              <Col>
                <p className='mb-0'>
                  Need additional help planning your stay? Email{' '}
                  <a href='mailto:info@youngisrael-stl.org'>info@youngisrael-stl.org</a> or call{' '}
                  <a href='tel:13147271880'>314-727-1880</a> and we will be happy to assist.
                </p>
              </Col>
            </Row>
          </Container>
        </TabPanel>
      </TabView>
    </>
  );
};

export default Community;
